import { useRef } from 'react';
import { IonButton, IonPopover, isPlatform } from '@ionic/react';
import { Share as CapacitorShare } from '@capacitor/share';
import { useClipboard } from '../utils/useClipboard';
import { useCanShare } from '../utils/useCanShare';
import { ShareIcon } from '../theme/Icons';
import { IThemeColor } from '../theme/IThemeColor';
import { Text } from '../components/Text';

export interface ISharePageButtonProps {
    'data-type': string;
    shareSubject: string; // used only by android/gmail and ios/mail
    shareBody: string;
    pageUrl?: string;
    buttonLabel?: string;
    color?: IThemeColor;
    iconColor?: IThemeColor;
    mode?: 'ios',
    shape?: 'round',
    size?: 'small';
    onMobileSharingOpened?: () => void;
    onLinkCopied?: () => void;
}

export const SharePageButton: React.FC<ISharePageButtonProps> = ({
    'data-type': dataType,
    shareSubject,
    shareBody,
    pageUrl,
    buttonLabel,
    color,
    iconColor,
    mode,
    shape,
    size,
    onMobileSharingOpened,
    onLinkCopied
}) => {
    const popoverRef = useRef<HTMLIonPopoverElement>(null);

    const canShare = useCanShare();

    const [copyToClipboard, justCopied] = useClipboard();

    const shareLink = async (evt: any) => {
        const url = pageUrl || window.location.href;

        if (canShare) {
            if (onMobileSharingOpened) {
                onMobileSharingOpened();
            }

            try {
                const shareInfo = { url, title: shareSubject, text: shareBody };
                if (isPlatform('capacitor')) {
                    await CapacitorShare.share(shareInfo);
                } else {
                    await navigator.share(shareInfo);
                }
            } catch (err: any) {
                // if the user closes the share sheet without sharing, we don't care
                // (in web it's "AbortError", in native it's "share canceled")
                if (err?.name !== 'AbortError' && !err?.toString().toLowerCase().includes('share canceled')) {
                    throw err;
                }
            }
        } else {
            // set popover event, so it opens above/below button
            popoverRef.current!.event = evt;

            copyToClipboard({ value: url, onCopied: () => {
                if (onLinkCopied) {
                    onLinkCopied();
                }
            } });
        }
    };

    return (
        <>
            <IonButton
                data-type={dataType}
                mode={mode}
                shape={shape}
                size={size}
                color={color}
                onClick={shareLink}
            >
                <div style={{display: 'flex', alignItems: 'center', gap: 5}}>
                    <ShareIcon color={iconColor || 'medium'} />
                    {buttonLabel || ''}
                </div>
            </IonButton>

            <IonPopover
                ref={popoverRef}
                keepContentsMounted={true}
                showBackdrop={false}
                isOpen={justCopied}
                data-type="link-copied-popover"
            >
                <div style={{display: 'flex', justifyContent: 'center', padding: 10}}>
                    <Text>Link copied to clipboard!</Text>
                </div>
            </IonPopover>
        </>
    );
};
